import { type FC, type MouseEventHandler } from 'react';
import { useSelector } from 'react-redux';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import classnames from 'classnames';

import dibsCss from 'dibs-css';

// components
import { PdpRespSidebarBlurbShipping } from './PdpRespSidebarBlurbShipping';
import { PdpRespSidebarBlurbReturnPolicy } from './PdpRespSidebarBlurbReturnPolicy';
import { useRenderBlurbs } from './hooks/useRenderBlurbs';
import { type RelayVariables, type Store } from '../../global/relayVariableTypes';
import { PdpSharedBlurb } from '../PdpSharedBlurbs/PdpSharedBlurb/PdpSharedBlurb';

import PdpSharedSidebarBuyerIncentives from '../shared/PdpSharedSidebarBuyerIncentives/PdpSharedSidebarBuyerIncentives';

import { type PdpRespSidebarBlurbs_viewer$data } from './__generated__/PdpRespSidebarBlurbs_viewer.graphql';
import { type PdpRespSidebarBlurbs_item$data } from './__generated__/PdpRespSidebarBlurbs_item.graphql';
import { type PdpRespSidebarBlurbs_user$data } from './__generated__/PdpRespSidebarBlurbs_user.graphql';
import { type PdpRespSidebarBlurbs_displayInfo$data } from './__generated__/PdpRespSidebarBlurbs_displayInfo.graphql';
import { type PdpRespSidebarBlurbs_sku$data } from './__generated__/PdpRespSidebarBlurbs_sku.graphql';

// styles
import styles from './PdpRespSidebarBlurbs.scss';

type Props = {
    viewer: PdpRespSidebarBlurbs_viewer$data;
    item: PdpRespSidebarBlurbs_item$data;
    user: PdpRespSidebarBlurbs_user$data | null | undefined;
    displayInfo: PdpRespSidebarBlurbs_displayInfo$data | null | undefined;
    sku: PdpRespSidebarBlurbs_sku$data | null | undefined;
    currency?: string;
    itemQuantity?: number;
    buyerIncentivesSidebarClick: MouseEventHandler;
};

const PdpRespSidebarBlurbsComponent: FC<Props> = ({
    currency = 'USD',
    viewer,
    item,
    user,
    displayInfo = null,
    sku,
    itemQuantity,
    buyerIncentivesSidebarClick,
}) => {
    const renderBlurbs = useRenderBlurbs({ item, displayInfo });
    const relayVariables = useSelector<Store, RelayVariables>(
        state => state.relayVariables.variables
    );
    if (!renderBlurbs) {
        return null;
    }

    return (
        <div className={classnames(styles.container, dibsCss.mbSmaller)}>
            <PdpRespSidebarBlurbShipping
                currency={currency}
                item={item}
                viewer={viewer}
                user={user}
                renderTooltip
                sku={relayVariables.isMultiSku ? sku : null}
                isClient={relayVariables.afterDisplay}
                itemQuantity={itemQuantity}
                shortLinkCta
            />

            <PdpSharedBlurb
                zeroPadding
                IconComponent={null}
                iconClass={classnames(dibsCss.mrXsmall, styles.icon)}
            >
                <PdpSharedSidebarBuyerIncentives
                    onDetailsClick={buyerIncentivesSidebarClick}
                    user={user}
                />
            </PdpSharedBlurb>

            <div className={classnames(dibsCss.hidden, styles.returnPolicyContainer)}>
                <PdpRespSidebarBlurbReturnPolicy item={item} />
            </div>
        </div>
    );
};

export const PdpRespSidebarBlurbs = createFragmentContainer(PdpRespSidebarBlurbsComponent, {
    viewer: graphql`
        fragment PdpRespSidebarBlurbs_viewer on Viewer {
            ...PdpRespSidebarBlurbShipping_viewer
        }
    `,
    item: graphql`
        fragment PdpRespSidebarBlurbs_item on Item {
            ...useRenderBlurbs_item
            ...PdpRespSidebarBlurbShipping_item
            ...PdpRespSidebarBlurbReturnPolicy_item
            ...useShowReturns_item
        }
    `,
    user: graphql`
        fragment PdpRespSidebarBlurbs_user on User {
            ...PdpRespSidebarBlurbShipping_user
            ...PdpSharedSidebarBuyerIncentives_user
        }
    `,
    displayInfo: graphql`
        fragment PdpRespSidebarBlurbs_displayInfo on ItemDisplayInfo {
            ...useRenderBlurbs_displayInfo
        }
    `,
    sku: graphql`
        fragment PdpRespSidebarBlurbs_sku on ItemSkuType {
            ... on ItemSkuType @include(if: $isMultiSku) {
                ...PdpRespSidebarBlurbShipping_sku
            }
        }
    `,
});
